import type { FormField } from '../../types';
import type { AnalyticsResponse } from './normalize';

/** Reach and stops at one schema position. */
export interface DropOffStep {
  position: number;
  fieldId: string;
  label: string;
  /** Respondents who got at least this far (completed responses included). */
  reached: number;
  /** Unfinished responses whose active step is here. */
  stopped: number;
  /** Responses the respondent ended early, placed at their last answered step. */
  ended: number;
}

/** Where unfinished conversations stopped, over schema position. */
export interface DropOff {
  steps: DropOffStep[];
  /** Responses that finished every step. */
  completed: number;
  /** Responses without progress (legacy rows). */
  unknown: number;
  total: number;
}

/** Reason the reducer writes when a respondent explicitly ends early. */
const REQUESTED_END = 'respondent_requested_end';

/** Where one response stopped. */
type Outcome = { type: 'completed' } | { type: 'unknown' } | { type: 'stopped' | 'ended'; fieldId: string; snapshotPosition: number };

/** Classify one response from its progress document. */
function outcomeOf(row: AnalyticsResponse): Outcome {
  const steps = row.conversation_progress?.steps;
  if (!steps?.length) return { type: 'unknown' };
  const firstEnded = steps.findIndex(step => step.reasonCode === REQUESTED_END);
  if (firstEnded !== -1) {
    const lastAnswered = steps.reduce((last, step, index) => step.state === 'completed' ? index : last, -1);
    const step = steps[lastAnswered === -1 ? firstEnded : lastAnswered];
    return { type: 'ended', fieldId: step.fieldId ?? step.stepId ?? '', snapshotPosition: step.schemaPosition };
  }
  const index = row.conversation_progress?.activeStepIndex;
  const active = index === null || index === undefined ? undefined : steps[index];
  if (active?.state === 'active') return { type: 'stopped', fieldId: active.fieldId ?? active.stepId ?? '', snapshotPosition: active.schemaPosition };
  return { type: 'completed' };
}

/**
 * Drop-off over the current schema: how many respondents reached each
 * position and how many stopped or ended there. A stop is placed at the
 * current position of its field, falling back to the snapshot position.
 */
export function buildDropOff(fields: FormField[], responses: readonly AnalyticsResponse[]): DropOff {
  const positions = new Map(fields.map((item, index) => [item.id, index]));
  const stops = fields.map(() => ({ stopped: 0, ended: 0 }));
  let completed = 0;
  let unknown = 0;
  for (const row of responses) {
    const outcome = outcomeOf(row);
    if (outcome.type === 'completed') completed++;
    else if (outcome.type === 'unknown') unknown++;
    else {
      const position = positions.get(outcome.fieldId) ?? outcome.snapshotPosition;
      const slot = stops[Math.min(Math.max(position, 0), stops.length - 1)];
      if (slot) slot[outcome.type]++;
    }
  }
  let beyond = completed;
  const steps = fields.map((item, index) => ({ position: index, fieldId: item.id, label: item.label, reached: 0, stopped: stops[index].stopped, ended: stops[index].ended }));
  for (let index = steps.length - 1; index >= 0; index--) {
    beyond += steps[index].stopped + steps[index].ended;
    steps[index].reached = beyond;
  }
  return { steps, completed, unknown, total: responses.length };
}
